import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ProjectResourcesDialog from './ProjectResourcesDialog';
import {
  canOpenProjectMaterials,
  projectMaterialsLabel,
} from '../utils/materialsLabels';
import { type GlsLandSaleItem, glsLinkedProjectId } from '../services/api';

const CARD_BASE_SX = {
  bgcolor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--color-shadow-card)',
  borderRadius: 2,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column' as const,
};

/**
 * Card for an unlinked GLS `ProjectLandSale` row. Materials dialog uses
 * `resourceSource="gls"` so EDMs / eBooks resolve via land-sale → EDMProject.
 */
export function GlsCard({ item }: { item: GlsLandSaleItem }) {
  const navigate = useNavigate();
  const [coverFailed, setCoverFailed] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  // Landscape covers use `fill` to stretch into the frame; portrait stays
  // `contain` so tall renders aren't cropped. Defaults to contain until
  // natural dimensions are known.
  const [coverIsLandscape, setCoverIsLandscape] = useState(false);
  const showCover = Boolean(item.coverImage) && !coverFailed;

  React.useEffect(() => {
    setCoverFailed(false);
    setCoverIsLandscape(false);
  }, [item.coverImage]);

  const locationLine =
    [item.region, item.district, item.planningArea]
      .filter((s): s is string => Boolean(s && s.trim()))
      .join(' | ') || item.location || null;

  const handleDetails = () => {
    navigate(`/project-essential/gls-land-sales/${encodeURIComponent(String(item.id))}`);
  };

  const linkedProjectId = glsLinkedProjectId(item);

  return (
    <>
      <Card
        sx={{
          ...CARD_BASE_SX,
          // Fill the carousel slide — fixed px widths overflow the
          // percentage-based slide flex on mobile/tablet and stack.
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          flexShrink: 1,
          boxSizing: 'border-box',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleDetails}
          aria-label={`Open ${item.name || item.id} details`}
          sx={{
            all: 'unset',
            display: 'block',
            width: '100%',
            cursor: 'pointer',
            '&:hover': { opacity: 0.92 },
            '&:focus-visible': {
              outline: '2px solid var(--color-accent)',
              outlineOffset: -2,
            },
          }}
        >
          {showCover ? (
            <CardMedia
              component="img"
              image={item.coverImage!}
              alt={item.name || String(item.id)}
              sx={{
                height: { xs: 170, sm: 200, md: 230, lg: 250 },
                objectFit: coverIsLandscape ? 'fill' : 'contain',
                objectPosition: 'center',
                bgcolor: 'var(--color-surface-elevated)',
              }}
              onLoad={(e) => {
                const img = e.currentTarget;
                setCoverIsLandscape(img.naturalWidth > img.naturalHeight);
              }}
              onError={() => setCoverFailed(true)}
            />
          ) : (
            <Box
              sx={{
                height: { xs: 170, sm: 200, md: 230, lg: 250 },
                bgcolor: 'var(--color-surface-elevated)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              No image
            </Box>
          )}
        </Box>
        <CardContent
          sx={{
            p: 1.5,
            pb: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: 0 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.3,
              mb: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.name || String(item.id)}
          </Typography>
          {locationLine && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocationOnIcon sx={{ fontSize: 13, color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--color-text-muted)',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {locationLine}
              </Typography>
            </Stack>
          )}
        </CardContent>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 1.25,
            px: 1.5,
            pt: 1.25,
            pb: 1.25,
            borderTop: '1px solid var(--color-border)',
            bgcolor: 'var(--color-surface-elevated)',
          }}
        >
          <Button
            onClick={handleDetails}
            variant="contained"
            fullWidth
            size="small"
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              py: 0.6,
              borderRadius: 1,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': { bgcolor: 'var(--color-accent-strong)' },
            }}
          >
            Project Details
          </Button>
          <Button
            onClick={() => {
              if (!canOpenProjectMaterials(item.hasMaterials)) return;
              setResourcesOpen(true);
            }}
            variant="outlined"
            fullWidth
            size="small"
            disabled={!canOpenProjectMaterials(item.hasMaterials)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.74rem',
              lineHeight: 1.2,
              py: 0.6,
              borderRadius: 1,
              borderColor: 'var(--color-accent)',
              color: 'var(--color-accent-strong)',
              bgcolor: 'transparent',
              '&:hover': {
                borderColor: 'var(--color-accent-strong)',
                bgcolor: 'var(--color-accent-soft)',
              },
            }}
          >
            {projectMaterialsLabel(item.hasMaterials)}
          </Button>
        </Stack>
      </Card>

      <ProjectResourcesDialog
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
        projectId={linkedProjectId || String(item.id)}
        projectName={item.name}
        resourceSource={linkedProjectId ? 'project' : 'gls'}
        isUpcomingGls
      />
    </>
  );
}
